import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon, PencilIcon, EyeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import { Campaign } from '../../services/campaignService';
import { replaceVariablesWithSampleData } from '../../utils/previewUtils';
import { highlightVariables } from '../../utils/variableHighlighting';

interface EditHistoryEntry {
  id: number;
  instruction: string;
  content_before: string;
  content_after: string;
  change_summary: string;
  personalization_level: string;
  version_number: number;
  created_at: string;
}

interface CampaignPreviewProps {
  campaign: Campaign;
  onClose: () => void;
  onSave?: (updatedCampaign: Campaign) => void;
}

const CampaignPreview: React.FC<CampaignPreviewProps> = ({ campaign, onClose, onSave }) => {
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [versionContent, setVersionContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableContent, setEditableContent] = useState(campaign.content_html || '');
  const [isPreviewMode, setIsPreviewMode] = useState(true); // Default to preview mode with sample data
  const [finalVersion, setFinalVersion] = useState<number | null>(campaign.final_version_number || null);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [editableSubject, setEditableSubject] = useState(campaign.subject);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadEditHistory = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/edit-history`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setEditHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading edit history:', error);
    }
  }, [campaign.id]);

  useEffect(() => {
    loadEditHistory();
  }, [loadEditHistory]);

  const loadVersionContent = async (versionNumber: number) => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/versions/${versionNumber}`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        setVersionContent(result.content_html);
        setSelectedVersion(versionNumber);
      }
    } catch (error) {
      console.error('Error loading version content:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateVersion = (direction: 'prev' | 'next') => {
    if (!selectedVersion || editHistory.length === 0) return;

    const currentIndex = editHistory.findIndex(entry => entry.version_number === selectedVersion);
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : editHistory.length - 1;
    } else {
      newIndex = currentIndex < editHistory.length - 1 ? currentIndex + 1 : 0;
    }

    const newVersion = editHistory[newIndex].version_number;
    loadVersionContent(newVersion);
  };

  const getCurrentContent = () => {
    let content = '';

    if (selectedVersion !== null && versionContent) {
      content = versionContent;
    } else {
      content = isEditMode ? editableContent : campaign.content_html;
    }

    content = content || '';

    // Apply preview mode variable replacement or variable highlighting
    if (isPreviewMode) {
      // In preview mode, replace variables with sample data
      return replaceVariablesWithSampleData(content);
    } else {
      // In edit mode, highlight variables with blue styling
      return highlightVariables(content);
    }
  };

  const handleSaveEdit = async () => {
    if (!onSave) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_html: editableContent
        })
      });

      if (response.ok) {
        const updatedCampaign = { ...campaign, content_html: editableContent };
        onSave(updatedCampaign);
        setHasUnsavedChanges(false); // Reset unsaved changes flag
        setIsEditMode(false);
        console.log('✅ HTML content saved successfully');
      } else {
        throw new Error('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('❌ Failed to save changes. Please try again.');
    }
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (hasUnsavedChanges && isEditMode) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmClose) {
        return; // Don't close if user cancels
      }
    }
    onClose();
  };

  const handleSetFinalVersion = async (versionNumber: number | null) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/set-final-version`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          version_number: versionNumber
        })
      });

      if (response.ok) {
        setFinalVersion(versionNumber);
        const versionText = versionNumber ? `Version ${versionNumber}` : 'Current version';
        alert(`✅ Final version set to ${versionText}. This version will be used for approval.`);
      } else {
        throw new Error('Failed to set final version');
      }
    } catch (error) {
      console.error('Error setting final version:', error);
      alert('❌ Failed to set final version. Please try again.');
    }
  };

  const handleSaveSubject = async () => {
    if (!onSave || editableSubject.trim() === '') {
      alert('Subject line cannot be empty');
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: editableSubject.trim()
        })
      });

      if (response.ok) {
        const updatedCampaign = { ...campaign, subject: editableSubject.trim() };
        onSave(updatedCampaign);
        setIsEditingSubject(false);
        alert('✅ Subject line updated successfully');
      } else {
        throw new Error('Failed to update subject line');
      }
    } catch (error) {
      console.error('Error updating subject:', error);
      alert('❌ Failed to update subject line. Please try again.');
    }
  };

  const handleCancelSubjectEdit = () => {
    setEditableSubject(campaign.subject);
    setIsEditingSubject(false);
  };

  const getContentTitle = () => {
    if (selectedVersion !== null) {
      const entry = editHistory.find(e => e.version_number === selectedVersion);
      return `Version ${selectedVersion}${entry ? ` - ${entry.change_summary}` : ''}`;
    }
    return 'Current Version';
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-hidden">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[85vh] flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-xl font-semibold text-gray-900">Campaign Preview</h2>
              {hasUnsavedChanges && isEditMode && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 animate-pulse">
                  ⚠️ Unsaved Changes
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">{campaign.name}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-md"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Version History Sidebar */}
          <div className="w-80 border-r flex flex-col">
            <div className="p-4 border-b">
              <h3 className="font-medium text-gray-900">Version History</h3>
              <p className="text-sm text-gray-600">Click to compare versions</p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Current Version */}
              <div
                className={`p-4 border-b ${
                  selectedVersion === null ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedVersion(null);
                    setVersionContent('');
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">Current Version</span>
                      {finalVersion === null && (
                        <CheckIcon className="h-4 w-4 text-green-600 ml-2" title="This is the final version" />
                      )}
                    </div>
                    {selectedVersion === null && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Viewing</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Latest campaign content</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(campaign.created_at || Date.now()).toLocaleString()}
                  </p>
                </div>

                {/* Set Final Version Button */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => handleSetFinalVersion(null)}
                    disabled={finalVersion === null}
                    className={`w-full text-xs px-3 py-2 rounded-md transition-colors ${
                      finalVersion === null
                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {finalVersion === null ? '✓ Final Version' : 'Set as Final Version'}
                  </button>
                </div>
              </div>

              {/* Edit History */}
              {editHistory.map((entry) => (
                <div
                  key={entry.id}
                  className={`p-4 border-b ${
                    selectedVersion === entry.version_number ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div
                    className="cursor-pointer"
                    onClick={() => loadVersionContent(entry.version_number)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">Version {entry.version_number}</span>
                        {finalVersion === entry.version_number && (
                          <CheckIcon className="h-4 w-4 text-green-600 ml-2" title="This is the final version" />
                        )}
                      </div>
                      {selectedVersion === entry.version_number && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Viewing</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      <strong>Instruction:</strong> {entry.instruction}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Change:</strong> {entry.change_summary}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {entry.personalization_level}
                      </span>
                      <p className="text-xs text-gray-500">
                        {new Date(entry.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Set Final Version Button */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleSetFinalVersion(entry.version_number)}
                      disabled={finalVersion === entry.version_number}
                      className={`w-full text-xs px-3 py-2 rounded-md transition-colors ${
                        finalVersion === entry.version_number
                          ? 'bg-green-100 text-green-800 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {finalVersion === entry.version_number ? '✓ Final Version' : 'Set as Final Version'}
                    </button>
                  </div>
                </div>
              ))}

              {editHistory.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  <p className="text-sm">No edit history available</p>
                  <p className="text-xs mt-1">This campaign hasn't been edited with AI yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Content Preview */}
          <div className="flex-1 flex flex-col">
            {/* Content Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-medium text-gray-900">{getContentTitle()}</h3>
                <div className="flex items-center space-x-2">
                  {isEditingSubject ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <span className="text-sm text-gray-600">Subject:</span>
                      <input
                        type="text"
                        value={editableSubject}
                        onChange={(e) => setEditableSubject(e.target.value)}
                        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter subject line..."
                        maxLength={200}
                      />
                      <button
                        onClick={handleSaveSubject}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelSubjectEdit}
                        className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">Subject: {campaign.subject}</p>
                      <button
                        onClick={() => setIsEditingSubject(true)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit subject line"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* Edit/Preview Toggle */}
                {selectedVersion === null && (
                  <div className="flex items-center space-x-2">
                    <div className="flex bg-gray-100 rounded-md p-1">
                      <button
                        onClick={() => setIsEditMode(false)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          !isEditMode
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <EyeIcon className="h-4 w-4 inline mr-1" />
                        Preview
                      </button>
                      <button
                        onClick={() => setIsEditMode(true)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          isEditMode
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <PencilIcon className="h-4 w-4 inline mr-1" />
                        Edit HTML
                      </button>
                    </div>

                    {/* Variable Preview Toggle */}
                    <div className="flex bg-gray-100 rounded-md p-1">
                      <button
                        onClick={() => setIsPreviewMode(true)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          isPreviewMode
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title="Show variables with sample data (e.g., Emma instead of {$child_name})"
                      >
                        Sample Data
                      </button>
                      <button
                        onClick={() => setIsPreviewMode(false)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          !isPreviewMode
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title="Show raw variables (e.g., {$child_name})"
                      >
                        Variables
                      </button>
                    </div>

                    {isEditMode && onSave && (
                      <button
                        onClick={handleSaveEdit}
                        className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
                          hasUnsavedChanges
                            ? 'bg-orange-600 text-white hover:bg-orange-700 animate-pulse'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {hasUnsavedChanges ? '⚠️ Save Changes' : '✅ Saved'}
                      </button>
                    )}
                  </div>
                )}

                {/* Version Navigation */}
                {selectedVersion !== null && editHistory.length > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigateVersion('prev')}
                      className="p-2 hover:bg-gray-100 rounded-md"
                      title="Previous version"
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600">
                      {editHistory.findIndex(e => e.version_number === selectedVersion) + 1} of {editHistory.length}
                    </span>
                    <button
                      onClick={() => navigateVersion('next')}
                      className="p-2 hover:bg-gray-100 rounded-md"
                      title="Next version"
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content Display */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : isEditMode ? (
                <div className="max-w-none space-y-4">
                  {/* HTML Editor */}
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="bg-gray-50 px-6 py-4 border-b">
                      <h4 className="font-medium text-gray-900">HTML Editor</h4>
                      <p className="text-sm text-gray-600">Edit the HTML content directly. Changes will be reflected in the preview.</p>
                    </div>
                    <div className="p-4">
                      <textarea
                        value={editableContent}
                        onChange={(e) => {
                          setEditableContent(e.target.value);
                          if (e.target.value !== campaign.content_html) {
                            setHasUnsavedChanges(true);
                          } else {
                            setHasUnsavedChanges(false);
                          }
                        }}
                        className="w-full h-96 p-4 font-mono text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                        placeholder="Enter your HTML content here..."
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="max-w-none space-y-4">
                  {/* Email Preview Style */}
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    {/* Email Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b">
                      <div className="text-sm text-gray-600">
                        <div className="mb-2">
                          <strong>From:</strong> Your School Name &lt;info@yourschool.edu&gt;
                        </div>
                        <div className="mb-2">
                          <strong>To:</strong> {campaign.segment_name || 'Campaign Recipients'}
                        </div>
                        <div>
                          <strong>Subject:</strong> {campaign.subject}
                        </div>
                      </div>
                    </div>

                    {/* Email Content */}
                    <div className="p-6">
                      <div
                        className="prose prose-sm max-w-none [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:mb-3 [&_ol]:mb-3 [&_li]:mb-1"
                        dangerouslySetInnerHTML={{ __html: getCurrentContent() }}
                      />
                    </div>
                  </div>

                  {/* Version Details */}
                  {selectedVersion !== null && (
                    <div className="mt-6 bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Version Details</h4>
                      {(() => {
                        const entry = editHistory.find(e => e.version_number === selectedVersion);
                        if (!entry) return null;

                        return (
                          <div className="space-y-2 text-sm">
                            <div>
                              <strong className="text-blue-800">Instruction:</strong>
                              <p className="text-blue-700">{entry.instruction}</p>
                            </div>
                            <div>
                              <strong className="text-blue-800">Changes Made:</strong>
                              <p className="text-blue-700">{entry.change_summary}</p>
                            </div>
                            <div className="flex items-center space-x-4 text-blue-600">
                              <span><strong>Personalization:</strong> {entry.personalization_level}</span>
                              <span><strong>Created:</strong> {new Date(entry.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Approval Details */}
                  {(campaign.approval_status === 'approved' || campaign.approval_status === 'rejected') && (
                    <div className="mt-6 bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Approval Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <strong className="text-blue-800">Status:</strong>
                          {campaign.approval_status === 'approved' ? (
                            <span className="inline-flex items-center text-green-700">
                              ✅ Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-red-700">
                              ❌ Rejected
                            </span>
                          )}
                        </div>

                        {campaign.approval_feedback && (
                          <div>
                            <strong className="text-blue-800">Feedback:</strong>
                            <div className="mt-1 p-2 bg-white rounded border text-blue-700 max-h-32 overflow-y-auto">
                              {campaign.approval_feedback}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-4 text-blue-600">
                          <span>
                            <strong>Reviewed By:</strong> {(campaign as any).approved_by_email || 'Unknown'}
                          </span>
                          {(campaign as any).approved_at && (
                            <span>
                              <strong>Date:</strong> {new Date((campaign as any).approved_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignPreview;